import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface BatteryType {
  id: string;
  name: string;
  capacity_kwh: number;
  power_kw: number;
  description?: string;
}

export interface Container {
  id: string;
  name: string;
  arrival_date?: string;
  location?: string;
  status: 'onderweg' | 'aangekomen' | 'in_gebruik' | 'leeg';
  capacity: number;
  max_batteries_50kw?: number;
  max_batteries_100kw?: number;
  notes?: string;
  created_at: string;
}

export interface Battery {
  id: string;
  container_id?: string;
  battery_type_id?: string;
  capacity_kwh: number;
  power_kw: number;
  status: 'vrij' | 'gereserveerd' | 'ingepland' | 'geplaatst' | 'te_bestellen';
  serial_number?: string;
  notes?: string;
  battery_type?: BatteryType;
}

export const useBatteryContainers = () => {
  const [containers, setContainers] = useState<Container[]>([]);
  const [batteries, setBatteries] = useState<Battery[]>([]);
  const [batteryTypes, setBatteryTypes] = useState<BatteryType[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchBatteryTypes = async () => {
    const { data, error } = await supabase
      .from('battery_types')
      .select('*')
      .order('capacity_kwh');

    if (error) {
      toast.error('Fout bij ophalen batterijtypen');
      return [];
    }
    return data || [];
  };

  const fetchContainers = async () => {
    const { data, error } = await supabase
      .from('containers')
      .select('*')
      .is('deleted_at', null)
      .order('arrival_date', { ascending: true });

    if (error) {
      toast.error('Fout bij ophalen containers');
      return [];
    }
    return data || [];
  };

  const fetchBatteries = async () => {
    const { data, error } = await supabase
      .from('batteries')
      .select(`
        *,
        battery_type:battery_types(*)
      `)
      .is('deleted_at', null)
      .order('created_at');

    if (error) {
      toast.error('Fout bij ophalen batterijen');
      return [];
    }
    return data || [];
  };

  const loadData = async () => {
    setLoading(true);
    const [typesData, containersData, batteriesData] = await Promise.all([
      fetchBatteryTypes(),
      fetchContainers(),
      fetchBatteries(),
    ]);
    
    setBatteryTypes(typesData);
    setContainers(containersData);
    setBatteries(batteriesData);
    setLoading(false);
  };

  useEffect(() => {
    loadData();

    // Subscribe to real-time updates
    const containersChannel = supabase
      .channel('containers-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'containers' }, () => {
        fetchContainers().then(setContainers);
      })
      .subscribe();

    const batteriesChannel = supabase
      .channel('batteries-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'batteries' }, () => {
        fetchBatteries().then(setBatteries);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(containersChannel);
      supabase.removeChannel(batteriesChannel);
    };
  }, []);

  const createContainer = async (
    container: Omit<Container, 'id' | 'created_at'>,
    batteryOptions?: { batteryTypeId: string; quantity: number }
  ) => {
    const { data, error } = await supabase
      .from('containers')
      .insert([container])
      .select()
      .single();

    if (error) {
      toast.error('Fout bij aanmaken container');
      throw error;
    }

    // If battery options provided, create batteries
    if (batteryOptions && data) {
      try {
        await createBatteries(data.id, batteryOptions.batteryTypeId, batteryOptions.quantity);
        toast.success('Container en batterijen aangemaakt');
      } catch (batteryError) {
        console.error('Error creating batteries:', batteryError);
        toast.warning('Container aangemaakt, maar fout bij toevoegen batterijen');
      }
    } else {
      toast.success('Container aangemaakt');
    }

    return data;
  };

  const createBattery = async (battery: Omit<Battery, 'id'>) => {
    const { data, error } = await supabase
      .from('batteries')
      .insert([battery])
      .select()
      .single();

    if (error) {
      toast.error('Fout bij aanmaken batterij');
      throw error;
    }

    return data;
  };

  const createBatteries = async (
    containerId: string,
    batteryTypeId: string,
    quantity: number
  ) => {
    const batteryType = batteryTypes.find(t => t.id === batteryTypeId);
    if (!batteryType) {
      toast.error('Batterijtype niet gevonden');
      throw new Error('Battery type not found');
    }

    const batteries: Omit<Battery, 'id'>[] = [];

    for (let i = 0; i < quantity; i++) {
      batteries.push({
        container_id: containerId,
        battery_type_id: batteryTypeId,
        capacity_kwh: batteryType.capacity_kwh,
        power_kw: batteryType.power_kw,
        status: 'vrij',
      });
    }

    const { data, error } = await supabase
      .from('batteries')
      .insert(batteries)
      .select();

    if (error) {
      toast.error('Fout bij aanmaken batterijen');
      throw error;
    }

    toast.success(`${quantity} batterij(en) toegevoegd`);
    return data;
  };

  const canAddBatteryToContainer = (
    container: Container,
    capacityKwh: number,
    existingBatteries: Battery[]
  ): boolean => {
    const containerBatteries = existingBatteries.filter(b => b.container_id === container.id);
    const current50kw = containerBatteries.filter(b => b.capacity_kwh === 50).length;
    const current100kw = containerBatteries.filter(b => b.capacity_kwh === 100).length;
    
    const max50kw = container.max_batteries_50kw || 16;
    const max100kw = container.max_batteries_100kw || 8;

    if (capacityKwh === 50) {
      return current50kw < max50kw;
    } else {
      return current100kw < max100kw;
    }
  };

  const updateBatteryStatus = async (batteryId: string, status: Battery['status']) => {
    const { error } = await supabase
      .from('batteries')
      .update({ status })
      .eq('id', batteryId);

    if (error) {
      toast.error('Fout bij updaten batterij status');
      throw error;
    }
  };

  const updateContainer = async (id: string, updates: Partial<Omit<Container, 'id' | 'created_at'>>) => {
    const { error } = await supabase
      .from('containers')
      .update(updates)
      .eq('id', id);

    if (error) throw error;

    await fetchContainers();
  };

  const deleteContainer = async (id: string) => {
    const { error } = await supabase
      .from('containers')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;

    await fetchContainers();
  };

  const updateBattery = async (id: string, updates: Partial<Omit<Battery, 'id' | 'created_at'>>) => {
    const { error } = await supabase
      .from('batteries')
      .update(updates)
      .eq('id', id);

    if (error) throw error;

    await fetchBatteries();
  };

  const deleteBattery = async (id: string) => {
    const { error } = await supabase
      .from('batteries')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;

    await fetchBatteries();
  };

  return {
    containers,
    batteries,
    batteryTypes,
    loading,
    refetch: loadData,
    createContainer,
    createBattery,
    createBatteries,
    updateBatteryStatus,
    updateContainer,
    deleteContainer,
    updateBattery,
    deleteBattery,
    canAddBatteryToContainer,
  };
};
